/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import Loading from '../../../../components/Loading';
import useStore from '../../../../store';
import { isNullOrBlank } from '../../../../utils/checker';
import { calculateIRCells } from '../../../../utils/ews-ir-utils';
import { toFormatCurrency, toNumberFromCurrency, toNumberFromPercent } from '../../../../utils/format';
import { checkSelectedActiveColumn, mappingKhmerNumber } from '../../../../utils/market-development';
import { getYear } from '../../utils';
import EWSLabels from '../ews-template-labels';
import { getCompanyTypesByFilterOptionValue, getDefaultFilterOptionValue } from '../../../../utils/ews';

const Q_REPORT = gql`
	query getAnnuallyEarlyWarningIRSummaryReport(
		$years: [Float!]
		$companyTypes: [InsuranceInstitutionsType!] = []
		$companyId: String
		$type: EarlyWarningTypeStatement!
	) {
		getAnnuallyEarlyWarningIRSummaryReport(years: $years, companyTypes: $companyTypes, companyId: $companyId, type: $type)
	}
`;

export default function InvestmentReportSpreadSheetTable({
	filterOptions = [],
	years = null,
	companyType = null,
	companyId = null,
	type = null,
	onReadyForExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState([]);
	const [tables, setTables] = useState([]);
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(load, [years, companyType, companyId, type, filterOptions]);

	useEffect(() => data?.getAnnuallyEarlyWarningIRSummaryReport && setDetail(data?.getAnnuallyEarlyWarningIRSummaryReport), [data]);

	useEffect(() => detail && calculateTables(detail), [detail]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function calculateTables() {
		if (detail?.result) {
			const localTables = Object.keys(detail?.result).reduce((tables, key) => {
				const localTables = tables;
				const tableCells = getCells({ code: 'SFP', labels: detail?.labels, tableCells: detail?.result[key] });
				if (tableCells) {
					localTables.push({ tableCells, key });
				}
				return localTables;
			}, []);
			const sorts = localTables?.sort((a, b) => (parseInt(a?.key) > parseInt(b?.key) ? 1 : -1));
			if (sorts?.length === 2) {
				const oldTableCells = sorts[0]?.tableCells;
				const newTableCells = sorts[1]?.tableCells;
				const key = 'Comparison %';
				const tableCells = newTableCells?.reduce((p, c, i) => {
					let lPre = p;
					const old0 = oldTableCells[i][0]?.value;
					const old1 = oldTableCells[i][1]?.value;
					const old2 = oldTableCells[i][2]?.value;
					const old3 = oldTableCells[i][3]?.value;
					const old4 = oldTableCells[i][4]?.value;
					const new0 = c[0]?.value;
					const new1 = c[1]?.value;
					const new2 = c[2]?.value;
					const new3 = c[3]?.value;
					const new4 = c[4]?.value;
					const isBlanked =
						isNullOrBlank(old0) ||
						isNullOrBlank(old1) ||
						isNullOrBlank(old2) ||
						isNullOrBlank(old3) ||
						isNullOrBlank(old4) ||
						isNullOrBlank(new0) ||
						isNullOrBlank(new1) ||
						isNullOrBlank(new2) ||
						isNullOrBlank(new3) ||
						isNullOrBlank(new4);
					if (isBlanked) {
						lPre.push([
							{ value: '', readOnly: true },
							{ value: '', readOnly: true },
							{ value: '', readOnly: true },
							{ value: '', readOnly: true },
							{ value: '', readOnly: true },
						]);
					} else {
						const oldNum0 = toNumberFromCurrency(old0);
						const oldNum1 = toNumberFromCurrency(old1);
						const oldNum2 = toNumberFromCurrency(old2);
						const oldNum3 = toNumberFromCurrency(old3);
						const oldNum4 = toNumberFromPercent(old4);
						const newNum0 = toNumberFromCurrency(new0);
						const newNum1 = toNumberFromCurrency(new1);
						const newNum2 = toNumberFromCurrency(new2);
						const newNum3 = toNumberFromCurrency(new3);
						const newNum4 = toNumberFromPercent(new4);
						lPre.push([
							{ value: toFormatCurrency(oldNum0 === 0 ? 0 : (newNum0 / oldNum0 - 1) * 100, null) + '%', readOnly: true },
							{ value: toFormatCurrency(oldNum1 === 0 ? 0 : (newNum1 / oldNum1 - 1) * 100, null) + '%', readOnly: true },
							{ value: toFormatCurrency(oldNum2 === 0 ? 0 : (newNum2 / oldNum2 - 1) * 100, null) + '%', readOnly: true },
							{ value: toFormatCurrency(oldNum3 === 0 ? 0 : (newNum3 / oldNum3 - 1) * 100, null) + '%', readOnly: true },
							{ value: toFormatCurrency(oldNum4 === 0 ? 0 : (newNum4 / oldNum4 - 1) * 100, null) + '%', readOnly: true },
						]);
					}
					return lPre;
				}, []);
				setTables([...sorts, { tableCells, key }]);
			} else {
				setTables(sorts);
			}
			onReadyForExport({ labels: detail?.labels, tables: localTables });
		}
	}

	function load() {
		const variables = {
			years: years ? JSON.parse(years).map((year) => parseInt(year)) : [getYear(null)],
			companyTypes: getCompanyTypes(companyType),
			companyId,
			type,
		};
		loadReport({ variables });
	}

	function getCompanyTypes(value) {
		return getCompanyTypesByFilterOptionValue(value ? value : getDefaultFilterOptionValue({ filterOptions }));
	}

	function getCells({ code = null, labels = [], tableCells = null }) {
		if (tableCells) {
			const localCells = labels?.map((label) => {
				const values = [
					{ value: 0, readOnly: true },
					{ value: 0, readOnly: true },
					{ value: 0, readOnly: true },
					{ value: 0, readOnly: true },
					{ value: '%', readOnly: true },
				];
				const labelObject = tableCells?.find((e) => e?.code === label?.code);
				return labelObject?.values || values;
			});
			const { cells } = calculateIRCells({
				code,
				cells: localCells,
				numLabel: labels?.length,
				readOnly: true,
			});
			const truncatedCells = cells.map((row) => {
				return row.map((cell, colIndex) => {
					return {
						...cell,
						value: (() => {
							if (false === isNullOrBlank(cell?.value)) {
								if ([0, 1, 2]?.includes(colIndex)) {
									return toFormatCurrency(cell?.value, null);
								} else if (colIndex === 3) {
									return toFormatCurrency(cell?.value);
								}
							}
							return cell?.value;
						})(),
					};
				});
			});
			return truncatedCells;
		}
		return null;
	}

	return (
		<>
			<Box>
				{loading && (
					<>
						<Loading />
					</>
				)}
				{!loading && !detail && (
					<>
						<Text color="gray.400" textAlign="center" mt={4}>
							{t('No Data')}
						</Text>
					</>
				)}
				{!loading && detail && (
					<>
						<Box className="ews-container">
							<div className="ews-4-row-head">{t('descriptionReport')}</div>
							<Flex>
								{tables.map((table, i) => (
									<table key={i} className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="5" className="ews-background-light-silver">
													{lang === 'kh' ? t(mappingKhmerNumber(table?.key)) : t(table?.key)}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td colSpan="4" className="ews-background-light-silver">
													{t('amount1')}
												</td>
												<td
													rowSpan="3"
													className={checkSelectedActiveColumn(active, i * 5 + 4) ? 'active-column' : 'ews-background-light-silver'}
												>
													{t('Percentage of Invested Asset')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td colSpan="3" className="ews-background-light-silver">
													{t('(In Million KHR)')}
												</td>
												<td
													rowSpan="2"
													className={checkSelectedActiveColumn(active, i * 5 + 3) ? 'active-column' : 'ews-background-light-silver'}
												>
													{t('USD')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td className={checkSelectedActiveColumn(active, i * 5 + 0) ? 'active-column' : 'ews-background-light-silver'}>
													{t('KHR')}
												</td>
												<td className={checkSelectedActiveColumn(active, i * 5 + 1) ? 'active-column' : 'ews-background-light-silver'}>
													{t('Foreign Currency')}
												</td>
												<td className={checkSelectedActiveColumn(active, i * 5 + 2) ? 'active-column' : 'ews-background-light-silver'}>
													{t('Total')}
												</td>
											</tr>
										</thead>
									</table>
								))}
							</Flex>
							<div>
								<EWSLabels type="EWS" labels={detail?.labels} active={active} />
								<Flex>
									{tables.map((table, i) => (
										<Spreadsheet
											data={table?.tableCells}
											onChange={(cells) => {
												const tableCells = table?.tableCells?.reduce((pre, cur, i) => {
													let localPre = pre;
													localPre.push([
														{ value: cur[0]?.value, readOnly: true },
														{ value: cur[1]?.value, readOnly: true },
														{ value: cur[2]?.value, readOnly: true },
														{ value: cur[3]?.value, readOnly: true },
														{ value: cur[4]?.value, readOnly: true },
													]);
													return localPre;
												}, []);
												const localTable = { ...table, tableCells };
												const localTables = tables;
												localTables[i] = localTable;
												setTables(localTables);
											}}
											onActivate={(e) => setActive({ ...e, column: i * 5 + e?.column, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									))}
								</Flex>
							</div>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
